import {
  addBusinessDocumentsService,
  addBusinessService,
  deactivateBusinessService,
  deleteBusinessDocumentsService,
  editBusinessMembersService,
  editBusinessService,
  getBusinessDetailsService,
  getBusinessDocumentsService,
  getBusinessesListService,
  getBusinessListForSelectionService,
} from '@/services/businesses';
import {
  AddBusinessDocumentFields,
  BusinessFormFields,
  BusinessForSelectionQueryParams,
  MemberForm,
  queryKeys,
} from '@/types/businesses';
import { SelectOption } from '@/types/inputs';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetBusinessesList = () => {
  const [searchParams] = useSearchParams();
  const params = searchParams.toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, params],
    queryFn: () => getBusinessesListService(params),
  });

  return {
    isSuccess,
    businesses: data?.businesses.records || [],
    aggregateInfo: data?.aggregateInfo,
    isBusinessesPending: isPending,
    totalRecordCount: data?.businesses.totalRecordCount || 0,
  };
};

export const useGetBusinessesListForSelection = (
  queryParams: BusinessForSelectionQueryParams,
  enabled = true,
) => {
  const params = new URLSearchParams(queryParams).toString();

  const { data: businessList = [], isPending: isBusinessListPending } =
    useQuery({
      queryFn: () => getBusinessListForSelectionService(params),
      queryKey: [queryKeys.list, queryKeys.selectionList, params],
      select: data => {
        const mappedItems: SelectOption[] = data
          ? data.map(business => {
              return {
                ...business,
                value: business.uuid,
                label: business.legalBusinessName,
              };
            })
          : [];

        return mappedItems;
      },
      gcTime: Infinity,
      staleTime: Infinity,
      enabled,
    });

  return { businessList, isBusinessListPending };
};

export const useGetBusinessDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.list, queryKeys.details, uuid],
    queryFn: () => getBusinessDetailsService(uuid),
    enabled: !!uuid,
  });

  return {
    businessDetails: data,
    isBusinessDetailsPending: isPending,
  };
};

export function useAddBusiness() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: BusinessFormFields) => addBusinessService(data),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    addBusiness: mutateAsync,
    isAddBusinessPending: isPending,
  };
}

export function useEditBusiness() {
  const queryClient = useQueryClient();
  const { mutateAsync: editBusiness, isPending: isEditBusinessPending } =
    useMutation({
      mutationFn: (data: BusinessFormFields) => editBusinessService(data),
      onSuccess: () => clearQueryCache(queryClient),
    });

  return {
    editBusiness,
    isEditBusinessPending,
  };
}

export function useEditBusinessMembers() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: MemberForm) => editBusinessMembersService(data),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    editBusinessMembers: mutateAsync,
    isEditBusinessMembersPending: isPending,
  };
}

export const useDeactivateBusiness = (uuid: string, enabled = false) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => deactivateBusinessService(uuid, false),
    onSuccess: () => clearQueryCache(queryClient),
  });
  const { data, isFetching: isCanDeactivateBusinessPending } = useQuery({
    queryKey: [queryKeys.preflight, uuid],
    queryFn: () => deactivateBusinessService(uuid, true),
    enabled,
  });

  return {
    canDeactivateBusiness: data?.canDeactivate || false,
    isCanDeactivateBusinessPending,
    deactivateBusiness: mutateAsync,
    isDeactivateBusinessPending: isPending,
  };
};

export const useGetBusinessDocuments = (uuid: string) => {
  const { data, isPending, refetch } = useQuery({
    queryKey: [queryKeys.documents, uuid],
    queryFn: () => getBusinessDocumentsService(uuid),
    enabled: !!uuid,
  });

  return {
    businessDocuments: data?.businessDocuments?.records || [],
    isBusinessDocumentsPending: isPending,
    totalRecordsCount: data?.businessDocuments?.totalRecordCount || 0,
    refetchDocuments: refetch,
  };
};

export function useAddBusinessDocument() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (document: AddBusinessDocumentFields) =>
      addBusinessDocumentsService(document),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.documents],
      });
    },
  });

  return {
    addBusinessDocument: mutateAsync,
    isAddBusinessDocument: isPending,
  };
}

export function useDeleteBusinessDocument() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (uuid: string) => deleteBusinessDocumentsService(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.documents],
      });
    },
  });

  return {
    deleteBusinessDocument: mutateAsync,
    isDeleteBusinessDocument: isPending,
  };
}
